import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { PaymentSystem } from '~/api/Order';
import { company, DEFAULT_COMPANIES } from '~/company/Company';
import { usePromocode } from '~/hooks/usePromocode';
import adyenService from '~/services/Adyen';

import { cartStore, checkoutStore } from '~/stores';

import Icon from '../Icon/Icon';

import type { PromocodeProps } from './interfaces';
import styles from './Promocode.module.scss';
import Promocode1 from './Variant1/Promocode';

const Promocode = ({
  className,
  placeholder,
  spinnerClassName,
  submitButtonClassName,
  disabled,
}: PromocodeProps) => {
  const { t } = useTranslation();

  const {
    promocodeVal,
    warningMsg,
    isLoading,
    handlePromocodeChange,
    handleDelPromocode,
    handleSubmit,
    promocodeExists,
  } = usePromocode({
    checkPercentageType: true,
    watchPaidWithDiscount: true,
  });

  if ([...DEFAULT_COMPANIES].includes(company.name)) {
    return <Promocode1 disabled={disabled} />;
  }

  return (
    <form
      data-form="promocode"
      className={classes(['promocode__form', styles.root, className])}
      onSubmit={handleSubmit}
    >
      <div
        className={classes([
          'promocode__form-control',
          promocodeVal.length &&
            cartStore.promocode.success === null &&
            !isLoading &&
            '_filled',
          cartStore.promocode.errorType === 'error' && !isLoading && '_error',
          (cartStore.promocode.errorType === 'warning' || warningMsg) &&
            !isLoading &&
            '_warning',
          cartStore.promocode.success === true &&
            !warningMsg &&
            !isLoading &&
            '_success',
        ])}
      >
        <div className="checkout-summary__promocode-content">
          <div className="input-container">
            <input
              data-input="promocode"
              className={classes([isLoading && '_loading', styles.input])}
              placeholder={placeholder || t('phrases:enterPromocode')}
              value={promocodeVal}
              onChange={handlePromocodeChange}
              disabled={isLoading || Boolean(disabled)}
            />
            {promocodeVal &&
              !isLoading &&
              company.promocodeInInputResetCheckout && (
                <button
                  className={classes([
                    'button',
                    '_no-padding',
                    '_no-color',
                    'button-icon',
                    isLoading && '_loading',
                    styles.resetButton,
                    styles.buttonIcon,
                  ])}
                  onClick={handleDelPromocode}
                  type="button"
                  disabled={isLoading || Boolean(disabled)}
                >
                  <Icon type="burger-close" size={28} />
                </button>
              )}
          </div>
          {company.promocodeInInputResetCheckout && (
            <button
              data-button="promocode"
              className={classes([
                'button',
                styles.button,
                submitButtonClassName,
              ])}
              type="submit"
              onClick={promocodeExists ? handleDelPromocode : undefined}
              disabled={
                isLoading ||
                Boolean(disabled) ||
                (checkoutStore.activePaymentSystem === PaymentSystem.adyen &&
                  adyenService.step === 2)
              }
            >
              {isLoading ? (
                <span className={classes(['spinner', spinnerClassName])} />
              ) : promocodeExists ? (
                <Icon size={24} type="trash" />
              ) : (
                <Icon type="arrow" size={24} />
              )}
            </button>
          )}
        </div>
        {!isLoading && cartStore.promocode.message && (
          <p className="promocode__form-control-result">
            {cartStore.promocode.message}
          </p>
        )}
      </div>
      {warningMsg && !isLoading && (
        <div className={classes(['alert _warning mt-16 z-1', styles.alert])}>
          {warningMsg}
        </div>
      )}
    </form>
  );
};

export default observer(Promocode);
